import { Button, ConfirmDialog, DialogTitle, Typography } from '@montugroup/design-system';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import type { AlertColor } from '@mui/material';
import { Box, CircularProgress, Stack, useTheme } from '@mui/material';

const SEVERITY_TO_ICON: Record<AlertColor, React.ElementType> = {
  info: InfoOutlined,
  error: ErrorOutline,
  warning: WarningAmberOutlined,
  success: CheckCircleOutline,
};

export type PrescriptionAssistantModalProps = {
  title: string;
  open?: boolean;
  onConfirm: () => void;
  body?: React.ReactNode;
  severity?: AlertColor;
  confirmLabel?: string;
  closeLabel?: string;
  isLoading?: boolean;
  loadingTitle?: string;
  loadingBody?: string;
  /**
   * Handler for modal dismissal. If not provided, the modal can only be closed
   * via the confirm action, and no cancel button will be displayed.
   */
  onClose?: () => void;
};

export const PrescriptionAssistantModal = ({
  title,
  onConfirm,
  open,
  body,
  severity,
  confirmLabel = 'Confirm',
  closeLabel = 'Cancel',
  isLoading = false,
  loadingTitle,
  loadingBody,
  onClose,
}: PrescriptionAssistantModalProps) => {
  const IconComponent = severity && SEVERITY_TO_ICON[severity];
  const theme = useTheme();

  // If isLoading and we’ve been given a title/body for loader, replace the body content with full loader
  const showBodyLoader = isLoading && (!!loadingTitle || !!loadingBody);

  return (
    <ConfirmDialog
      open={!!open}
      onClose={isLoading ? undefined : onClose}
      PaperProps={{
        sx: { p: theme.spacing(6) },
      }}
      title={
        showBodyLoader ? undefined : (
          <DialogTitle component="div" sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center' }}>
            {IconComponent && <IconComponent color={severity} />}
            <Typography
              variant="h5"
              sx={{ color: severity ? theme.palette[severity].dark : theme.palette.primary.main }}
            >
              {title}
            </Typography>
          </DialogTitle>
        )
      }
      actions={
        showBodyLoader ? undefined : (
          <Box sx={{ display: 'flex', gap: theme.spacing(2), alignItems: 'center', justifyContent: 'flex-end' }}>
            {!!onClose && (
              <Button
                variant="text"
                color="primary"
                size="large"
                disabled={isLoading}
                sx={{ borderRadius: theme.spacing(1) }}
                onClick={onClose}
              >
                {closeLabel}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ borderRadius: theme.spacing(1) }}
              onClick={onConfirm}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
            >
              {confirmLabel}
            </Button>
          </Box>
        )
      }
    >
      <Box sx={{ width: theme.spacing(140), maxWidth: '100%' }}>
        {showBodyLoader ? (
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress size={26} sx={{ mb: 4 }} />
            {loadingTitle && (
              <Typography variant="h5" sx={{ mb: 4, color: theme.palette.primary.main }}>
                {loadingTitle}
              </Typography>
            )}
            {loadingBody && (
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {loadingBody}
              </Typography>
            )}
          </Stack>
        ) : (
          <Box sx={{ color: theme.palette.text.secondary }}>{body}</Box>
        )}
      </Box>
    </ConfirmDialog>
  );
};
